import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { AppHeader } from '~/components/layout/AppHeader';
import { BrandedTransitionScreen } from '~/components/ui/BrandedTransitionScreen';
import { ErrorState } from '~/components/ui/ErrorState';
import { YakSplashScreen } from '~/components/ui/YakSplashScreen';
import { AuthProvider } from '~/contexts/AuthContext';
import { HeaderProvider } from '~/contexts/HeaderContext';
import { useProfile } from '~/hooks/useProfile';
import { apiClient } from '~/lib/api';
import { resolveDomain } from '~/lib/domain';
import type { PublicLandingPage } from '~/types';
import { PublicLandingPageSchema } from '~/types/schemas';

export const Route = createFileRoute('/$slug')({
  beforeLoad: async ({ params, context }) => {
    const { slug: pathSlug } = params;

    // SSR guard: window is not available during prerendering
    // Return minimal context and let client handle domain resolution
    if (typeof window === 'undefined') {
      return { slug: pathSlug };
    }

    // Resolve domain: custom domains use hostname as slug, primary hosts use path
    const { type, value } = resolveDomain(
      window.location.hostname,
      window.location.pathname,
    );

    const slug = type === 'custom_domain' ? value : pathSlug;

    // Fetch and cache landing page data for all child routes
    const landingPage: PublicLandingPage = await context.queryClient.ensureQueryData({
      queryKey: ['landingPage', slug],
      queryFn: async () => {
        const data = await apiClient<unknown>(
          `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug)}`,
        );
        return PublicLandingPageSchema.parse(data) as PublicLandingPage;
      },
      staleTime: 1000 * 60 * 10, // 10 minutes
    });

    return {
      slug,
      landingPage,
    };
  },
  component: SlugLayout,
  pendingComponent: () => <YakSplashScreen />,
  errorComponent: ({ error, reset }) => (
    <ErrorState
      message={error?.message || 'Failed to load this page. Please try again.'}
      onRetry={reset}
    />
  ),
});

function SlugLayout() {
  // Initialize to false for SSR consistency, then show transition on client mount
  const [showTransition, setShowTransition] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const context = Route.useRouteContext();

  // Show transition only after client mount (avoids hydration mismatch)
  useEffect(() => {
    setIsMounted(true);
    if (context.landingPage) {
      setShowTransition(true);
    }
  }, [context.landingPage]);

  const handleTransitionComplete = useCallback(() => {
    setShowTransition(false);
  }, []);

  // Safety timeout: ensure transition clears even if callback fails
  useEffect(() => {
    if (!showTransition) return;
    const safetyTimer = setTimeout(() => {
      setShowTransition(false);
    }, 3000);
    return () => clearTimeout(safetyTimer);
  }, [showTransition]);

  // During SSR or initial mount, show loading state
  if (!isMounted || !context.landingPage) {
    return <YakSplashScreen />;
  }

  const { landingPage, slug } = context;

  return (
    <>
      {showTransition ? (
        <BrandedTransitionScreen
          branding={landingPage.branding}
          duration={1000}
          onTransitionComplete={handleTransitionComplete}
        />
      ) : (
        <AuthProvider slug={slug}>
          <HeaderProvider>
            <AuthenticatedLayout slug={slug} />
          </HeaderProvider>
        </AuthProvider>
      )}
    </>
  );
}

/**
 * Inner layout that uses useProfile() for reactive auth state.
 * This must be inside AuthProvider so TanStack Query context is available.
 * When login invalidates the profile cache, this re-renders and shows AppHeader.
 */
function AuthenticatedLayout({ slug }: { slug: string }) {
  const { data: profile } = useProfile();

  return (
    <>
      {profile && slug && <AppHeader slug={slug} />}
      <Outlet />
    </>
  );
}

// Type augmentation for child route access to context
declare module '@tanstack/react-router' {
  interface RouteContext {
    slug?: string;
    landingPage?: PublicLandingPage;
  }
}
